/**
 * The hold recovery piece: its behavior half reads the shared hold
 * (useWebHold); its standard look is the app's catch-after dialog.
 */
import { CatchAfterOverlay } from '@/components/SessionRecovery/CatchAfterOverlay';
import { formatWhen } from '@/pages/SessionDetailPage/detailFormat';

import { partsOf, pieceRoot, usePieceOptions, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone, keepTimes } from '../../format';
import type { WebHold } from '../../hooks/useWebHold';
import { contract, type HoldRecoveryActions, type HoldRecoveryState } from './contract';

const part = partsOf(contract);

export type HoldRecoveryRuntime = PieceRuntime<typeof contract, HoldRecoveryState, HoldRecoveryActions>;

export function useHoldRecovery(hold: WebHold, startsAt: string | null): HoldRecoveryRuntime {
  const options = usePieceOptions(contract, undefined);
  const phase = hold.phase === 'prompt' || hold.phase === 'reheld' || hold.phase === 'taken' ? hold.phase : 'none';
  return {
    state: {
      phase,
      whenText: startsAt ? keepTimes(formatWhen(startsAt, getClientTimezone())) : null,
      recovering: hold.recovering,
      error: hold.recoverError,
    },
    actions: { recover: hold.recover, repick: hold.repick },
    options,
  };
}

export function HoldRecoveryView({ state, actions }: HoldRecoveryRuntime) {
  if (state.phase === 'none') return null;
  return (
    <div {...pieceRoot(contract)} {...part('dialog')}>
      <CatchAfterOverlay
        phase={state.phase}
        whenText={state.whenText}
        recovering={state.recovering}
        errorMessage={state.error}
        onRecover={actions.recover}
        onRepick={actions.repick}
      />
    </div>
  );
}
